import React, { ReactElement, Suspense, useEffect, useState } from 'react';
import { useHistory, useLocation, Redirect, Switch, Route } from 'react-router-dom';
import {
    Tabs,
    Tab,
    Container,
    AppBar,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem,
} from '@mui/material';
import { styled } from '@mui/system';
import { UseAdminStatus } from '../../contexts/AdminContext';
import OrdersList from './modules/orders/scenes/OrdersList';
import OrganizationsList from './modules/organizations/scenes/OrganizationsList';
import PodList from './modules/pods/scenes/PodList';
import PodDashboard from './modules/pods/scenes/PodDashboard';
import PhysicalProductList from './modules/physicalProducts/scenes/PhysicalProductList';
import SubscriptionList from './modules/subscriptions/scenes/SubscriptionList';
import CommissionList from './modules/commissions/scenes/CommissionList';
import CompPlanReport from './modules/commissions/scenes/CompPlanReport';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import NetworkingPayoutList from './modules/networkingPayoutLogs/scenes/NetworkingPayoutList';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MarketplaceItemsList from './modules/marketplace/scenes/MarketplaceItemsList';
import Badge from '@mui/material/Badge';
import { AccountTree, Groups, SpeakerPhone } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import BinaryTreeAdmin from './modules/binaryTree/scenes/BinaryTreeAdmin';
import AthleteList from './modules/athletes/scenes/AthleteList';
import UsersList from './modules/users/scenes/UsersList';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import KPIDashboard from './modules/kpi/scenes/KPIDashboard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ServiceScene from './modules/service/scenes/ServiceScene';
import IntegrationsList from './modules/integrations/scenes/IntegrationsList';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RouterIcon from '@mui/icons-material/Router';
import HubIcon from '@mui/icons-material/Hub';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import LpsPage from './modules/lps/scenes/LpsPage';
import Fp1Page from './modules/fp1/scenes/Fp1Page';
import AdminSalesFunnelDashboard from './modules/salesFunnel/scenes/AdminSalesFunnelDashboard';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PollIcon from '@mui/icons-material/Poll';

const SurveysList = React.lazy(() => import('./modules/surveys/scenes/SurveysList'));
const SurveyCreate = React.lazy(() => import('./modules/surveys/scenes/SurveyCreate'));
const SurveyDetail = React.lazy(() => import('./modules/surveys/scenes/SurveyDetail'));
const WhatsNewList = React.lazy(() => import('./modules/whats-new/scenes/WhatsNewList'));
const WhatsNewEdit = React.lazy(() => import('./modules/whats-new/scenes/WhatsNewEdit'));

const StyledTab = styled(Tab)(({ theme }) => ({
    width: '20%',
    minWidth: 'auto',
    minHeight: '40px', // Keep the minimum height
    height: '40px', // Keep the fixed height
    fontSize: '0.6rem', // Reduced font size for the text
    padding: '10px 8px 4px', // Add more top padding to lower the content
    '& .MuiTab-wrapper': {
        display: 'flex',
        flexDirection: 'row', // Ensure icon and text are in a row
        justifyContent: 'center',
        alignItems: 'center', // Center items vertically
        height: '100%', // Full height of the tab
    },
    '& .MuiTab-iconWrapper': {
        marginBottom: '2px', // Set the margin-bottom to 2px as requested
        marginRight: theme.spacing(1),
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1.2rem', // Base size for the icon
    },
}));

export default function AdminBasePage(): ReactElement {
    const history = useHistory();
    const location = useLocation();
    const { isAdmin, adminLoaded } = UseAdminStatus();
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
    const [tabValue, setTabValue] = useState(0);
    const [entitiesMenuAnchor, setEntitiesMenuAnchor] = useState<null | HTMLElement>(null);
    const [financialMenuAnchor, setFinancialMenuAnchor] = useState<null | HTMLElement>(null);
    const { pendingOrders, pendingMarketplaceResellRequests, pendingSubscriptionChangeRequests } =
        UseAdminStatus();

    useEffect(() => {
        console.debug('pending orders', pendingOrders);
        console.debug('pending resell requests', pendingMarketplaceResellRequests);
        console.debug('pending subscription change requests', pendingSubscriptionChangeRequests);
    }, [pendingOrders, pendingMarketplaceResellRequests, pendingSubscriptionChangeRequests]);

    useEffect(() => {
        if (location.pathname === '/admin') {
            history.replace('/admin/fp1');
        }
    }, [location.pathname, history]);

    useEffect(() => {
        // Tab indices: 0=FP1, 1=LPS, 2=Entities, 3=Financial, 4=Surveys, 5=What's New, 6=Orders(hidden), 7=Pods
        if (location.pathname.includes('/admin/fp1')) setTabValue(0);
        else if (location.pathname.includes('/admin/lps')) setTabValue(1);
        else if (location.pathname.includes('/admin/orgs')) setTabValue(2);
        else if (location.pathname.includes('/admin/athletes')) setTabValue(2);
        else if (location.pathname.includes('/admin/users')) setTabValue(2);
        else if (location.pathname.includes('/admin/service')) setTabValue(2);
        else if (location.pathname.includes('/admin/integrations')) setTabValue(2);
        else if (location.pathname.includes('/admin/products')) setTabValue(3);
        else if (location.pathname.includes('/admin/subscriptions')) setTabValue(3);
        else if (location.pathname.includes('/admin/commissions')) setTabValue(3);
        else if (location.pathname.includes('/admin/comp-plan')) setTabValue(3);
        else if (location.pathname.includes('/admin/marketplace')) setTabValue(3);
        else if (location.pathname.includes('/admin/payout-logs')) setTabValue(3);
        else if (location.pathname.includes('/admin/binary-tree')) setTabValue(3);
        else if (location.pathname.includes('/admin/kpi')) setTabValue(3);
        else if (location.pathname.includes('/admin/sales-funnel')) setTabValue(3);
        else if (location.pathname.includes('/admin/surveys')) setTabValue(4);
        else if (location.pathname.includes('/admin/whats-new')) setTabValue(5);
        else if (location.pathname.includes('/admin/orders')) setTabValue(6);
        else if (location.pathname.includes('/admin/pods')) setTabValue(7);
        else setTabValue(0);
    }, [location.pathname]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        // Tab indices: 0=FP1, 1=LPS, 2=Entities(menu), 3=Financial(menu), 4=Surveys, 5=What's New, 6=Orders(hidden), 7=Pods
        if (newValue === 2) {
            setEntitiesMenuAnchor(event.currentTarget as HTMLElement);
            return;
        }
        if (newValue === 3) {
            setFinancialMenuAnchor(event.currentTarget as HTMLElement);
            return;
        }
        const paths: (string | null)[] = [
            'fp1',
            'lps',
            null,
            null,
            'surveys',
            'whats-new',
            'orders',
            'pods',
        ];
        if (paths[newValue]) {
            history.push(`/admin/${paths[newValue]}`);
        }
    };

    const handleEntitiesMenuClick = (path: string) => {
        setEntitiesMenuAnchor(null);
        history.push(`/admin/${path}`);
    };

    const handleFinancialMenuClick = (path: string) => {
        setFinancialMenuAnchor(null);
        history.push(`/admin/${path}`);
    };

    const badgeStyle = {
        '& .MuiBadge-badge': {
            fontSize: '0.5rem',
            height: '14px',
            minWidth: '14px',
            padding: '0 4px',
            top: '20%',
            right: '-15%',
        },
    };

    if (!adminLoaded) {
        return <></>;
    }

    return isAdmin ? (
        <>
            <AppBar position="sticky" color="default" elevation={1}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="admin navigation tabs"
                >
                    {/* Visible tabs — order: FP1, LPS, Entities, Financial, Surveys */}
                    <StyledTab
                        icon={
                            <Badge badgeContent={pendingOrders} color="error" sx={badgeStyle}>
                                <HubIcon />
                            </Badge>
                        }
                        label={isNarrow ? '' : 'FP1'}
                    />
                    <StyledTab icon={<RouterIcon />} label={isNarrow ? '' : 'LPS'} />
                    <StyledTab icon={<Groups />} label={isNarrow ? '' : 'Entities'} />
                    <StyledTab
                        icon={
                            <Badge
                                badgeContent={
                                    pendingSubscriptionChangeRequests +
                                    pendingMarketplaceResellRequests
                                }
                                color="error"
                                sx={badgeStyle}
                            >
                                <AttachMoneyIcon />
                            </Badge>
                        }
                        label={isNarrow ? '' : 'Financial'}
                    />
                    <StyledTab icon={<PollIcon />} label={isNarrow ? '' : 'Surveys'} />
                    <StyledTab
                        icon={<FiberNewIcon />}
                        label={isNarrow ? '' : "What's New"}
                        aria-label="What's New"
                    />
                    {/* Orders tab — hidden, kept to preserve routing logic */}
                    <StyledTab
                        icon={
                            <Badge badgeContent={pendingOrders} color="error" sx={badgeStyle}>
                                <DescriptionIcon />
                            </Badge>
                        }
                        label={isNarrow ? '' : 'Orders'}
                        sx={{ display: 'none' }}
                    />
                    <StyledTab icon={<SpeakerPhone />} label={isNarrow ? '' : 'Pods'} />
                </Tabs>
                <Menu
                    anchorEl={entitiesMenuAnchor}
                    open={Boolean(entitiesMenuAnchor)}
                    onClose={() => setEntitiesMenuAnchor(null)}
                >
                    <MenuItem onClick={() => handleEntitiesMenuClick('orgs')}>
                        <BusinessIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Organizations
                    </MenuItem>
                    <MenuItem onClick={() => handleEntitiesMenuClick('athletes')}>
                        <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Athletes
                    </MenuItem>
                    <MenuItem onClick={() => handleEntitiesMenuClick('users')}>
                        <PeopleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Users
                    </MenuItem>
                    <MenuItem onClick={() => handleEntitiesMenuClick('service')}>
                        <SupportAgentIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Service
                    </MenuItem>
                    <MenuItem onClick={() => handleEntitiesMenuClick('integrations')}>
                        <IntegrationInstructionsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Integrations
                    </MenuItem>
                </Menu>
                <Menu
                    anchorEl={financialMenuAnchor}
                    open={Boolean(financialMenuAnchor)}
                    onClose={() => setFinancialMenuAnchor(null)}
                >
                    <MenuItem onClick={() => handleFinancialMenuClick('products')}>
                        <InventoryIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Products
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('subscriptions')}>
                        <CalendarTodayIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Subscriptions
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('commissions')}>
                        <AttachMoneyIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Commissions
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('comp-plan')}>
                        <BarChartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Comp Plan
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('marketplace')}>
                        <VideoLibraryIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Marketplace
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('payout-logs')}>
                        <RequestPageIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Payout Logs
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('binary-tree')}>
                        <AccountTree sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Binary Tree
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('kpi')}>
                        <BarChartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        KPI
                    </MenuItem>
                    <MenuItem onClick={() => handleFinancialMenuClick('sales-funnel')}>
                        <FilterAltIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        Sales Funnel
                    </MenuItem>
                </Menu>
            </AppBar>
            <Container maxWidth={false} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
                    <Switch>
                        <Route path="/admin/orders" component={OrdersList} />
                        <Route path="/admin/orgs" component={OrganizationsList} />
                        <Route path="/admin/athletes" component={AthleteList} />
                        <Route path="/admin/users" component={UsersList} />
                        <Route exact path="/admin/pods/dashboard" component={PodDashboard} />
                        <Route path="/admin/pods" component={PodList} />
                        <Route path="/admin/products" component={PhysicalProductList} />
                        <Route path="/admin/subscriptions" component={SubscriptionList} />
                        <Route path="/admin/commissions" component={CommissionList} />
                        <Route path="/admin/comp-plan" component={CompPlanReport} />
                        <Route path="/admin/marketplace" component={MarketplaceItemsList} />
                        <Route path="/admin/payout-logs" component={NetworkingPayoutList} />
                        <Route path="/admin/binary-tree" component={BinaryTreeAdmin} />
                        <Route path="/admin/kpi" component={KPIDashboard} />
                        <Route path="/admin/service" component={ServiceScene} />
                        <Route path="/admin/integrations" component={IntegrationsList} />
                        <Route path="/admin/sales-funnel" component={AdminSalesFunnelDashboard} />
                        <Route path="/admin/lps" component={LpsPage} />
                        <Route path="/admin/fp1" component={Fp1Page} />
                        <Route exact path="/admin/surveys" component={SurveysList} />
                        <Route path="/admin/surveys/create" component={SurveyCreate} />
                        <Route path="/admin/surveys/:uuid" component={SurveyDetail} />
                        <Route exact path="/admin/whats-new" component={WhatsNewList} />
                        <Route path="/admin/whats-new/new" component={WhatsNewEdit} />
                        <Route path="/admin/whats-new/:uuid" component={WhatsNewEdit} />
                    </Switch>
                </Suspense>
            </Container>
        </>
    ) : (
        <Redirect to="/" />
    );
}
